import {
  FieldDefinition,
  FieldBuilder,
  MenuCategories,
  MenuSubcategories,
  MENU_CATEGORY_KEY,
  MENU_SUBCATEGORY_KEY,
  MENU_NAME_SUFFIX,
  GeographicCoordinateAxis,
} from '@zendra/shared';
import { LocationTypeService } from '../proxy/planning/controllers/location-type.service';
import { Zone } from '../zone/zone';
import { Subregion } from '../subregion/subregion';
import { Country } from '../country/country';
import { Department } from '../department/department';
import { Deposit } from '../deposit/deposit';
import { Firebreak } from '../firebreak/firebreak';
import { LoadProperty } from '../load-property/load-property';
import { LocalCorridor } from '../local-corridor/local-corridor';
import { Locality } from '../locality/locality';
import { LocalRoad } from '../local-road/local-road';
import { Logyard } from '../logyard/logyard';
import { Nursery } from '../nursery/nursery';
import { OtherUse } from '../other-use/other-use';
import { Property } from '../property/property';
import { Region } from '../region/region';
import { Register } from '../register/register';
import { Stand } from '../stand/stand';
import { ThirdPartyProperty } from '../third-party-property/third-party-property';
import { EleIs } from '../ele-is/ele-is';
import { Fence } from '../fence/fence';
import { FirebreakBlock } from '../firebreak-block/firebreak-block';
import { Helmet } from '../helmet/helmet';
import { InternalRoad } from '../internal-road/internal-road';
import { Office } from '../office/office';
import { CadastralSection } from '../cadastral-section/cadastral-section';
import { Mill } from '@planning/mill/mill';
import { Pile } from '@planning/pile/pile';
import { MillSector } from '@planning/mill-sector/mill-sector';
import { ThirdPartyDestination } from '../third-party-destination/third-party-destination';
FieldBuilder.setNamespace('Planning');

export const LOCATION_FIELDS: FieldDefinition[] = [
  FieldBuilder.text('name', 'Entity.Location.Name')
    .required()
    .minLength(1)
    .maxLength(200)
    .build(),
  FieldBuilder.checkbox('isOrigin', 'Entity.Location.IsOrigin').build(),
  FieldBuilder.checkbox('isDestination', 'Entity.Location.IsDestination').build(),
  FieldBuilder.number('latitude', 'Entity.Location.Latitude')
    .geographicCoordinate(GeographicCoordinateAxis.Latitude, 9, 21)
    .build(),
  FieldBuilder.number('longitude', 'Entity.Location.Longitude')
    .geographicCoordinate(GeographicCoordinateAxis.Longitude, 9, 21)
    .build(),
  FieldBuilder.number('area', 'Entity.Location.Area')
    .decimal(2,10)
    .build(),
  FieldBuilder.checkbox('isActive', 'Entity.Location.IsActive').build(),
  FieldBuilder.foreignKey(
    'locationTypeId',
    'Entity.LocationType',
    LocationTypeService
  )
    .required()
    .displayFromNested('locationType.name')
    .sortBy('locationType.name')
    .build(),
];

export const LOCATION_CONFIG = {
  entityName: 'Location',
  localizationKey: 'Planning::Entity.Location',
  fields: LOCATION_FIELDS,
  permission: 'PlanningLocation.Locations',
  showInMenu: true,
  menuCategory: MENU_CATEGORY_KEY + MenuCategories.Planning + MENU_NAME_SUFFIX,
  menuSubcategory: MENU_SUBCATEGORY_KEY + MenuSubcategories.Location + MENU_NAME_SUFFIX,
  getChildren: (row: any, allChildren: any[]) => {
    const locationType = row?.locationType;
    if (!locationType) {
      return [];
    }
    const locationTypeName = locationType.entityName || locationType.name;
    if (!locationTypeName) {
      return [];
    }
    const lowerTypeName = locationTypeName.toLowerCase();
    return allChildren.filter(child => child.entityName.toLowerCase() === lowerTypeName);
  },
  children: [
    {
      entityName: 'SubRegion',
      foreignKeyField: 'locationId',
      localizationKey: 'Planning::Entity.Subregion',
      component: Subregion,
    },
    {
      entityName: 'Zone',
      foreignKeyField: 'locationId',
      localizationKey: 'Planning::Entity.Zone',
      component: Zone,
    },
    {
      entityName: 'Country',
      foreignKeyField: 'locationId',
      localizationKey: 'Planning::Entity.Country',
      component: Country,
    },
    {
      entityName: 'Department',
      foreignKeyField: 'locationId',
      localizationKey: 'Planning::Entity.Department',
      component: Department,
    },
    {
      entityName: 'Deposit',
      foreignKeyField: 'locationId',
      localizationKey: 'Planning::Entity.Deposit',
      component: Deposit,
    },
    {
      entityName: 'Firebreak',
      foreignKeyField: 'locationId',
      localizationKey: 'Planning::Entity.Firebreak',
      component: Firebreak,
    },
    {
      entityName: 'LoadProperty',
      foreignKeyField: 'locationId',
      localizationKey: 'Planning::Entity.LoadProperty',
      component: LoadProperty,
    },
    {
      entityName: 'LocalCorridor',
      foreignKeyField: 'locationId',
      localizationKey: 'Planning::Entity.LocalCorridor',
      component: LocalCorridor,
    },
    {
      entityName: 'Locality',
      foreignKeyField: 'locationId',
      localizationKey: 'Planning::Entity.Locality',
      component: Locality,
    },
    {
      entityName: 'LocalRoad',
      foreignKeyField: 'locationId',
      localizationKey: 'Planning::Entity.LocalRoad',
      component: LocalRoad,
    },
    {
      entityName: 'Logyard',
      foreignKeyField: 'locationId',
      localizationKey: 'Planning::Entity.Logyard',
      component: Logyard,
    },
    {
      entityName: 'Mill',
      foreignKeyField: 'locationId',
      localizationKey: 'Planning::Entity.Mill',
      component: Mill,
    },
    {
      entityName: 'MillSector',
      foreignKeyField: 'locationId',
      localizationKey: 'Planning::Entity.MillSector',
      component: MillSector,
    },
    {
      entityName: 'Nursery',
      foreignKeyField: 'locationId',
      localizationKey: 'Planning::Entity.Nursery',
      component: Nursery,
    },
    {
      entityName: 'OtherUse',
      foreignKeyField: 'locationId',
      localizationKey: 'Planning::Entity.OtherUse',
      component: OtherUse,
    },
    {
      entityName: 'Property',
      foreignKeyField: 'locationId',
      localizationKey: 'Planning::Entity.Property',
      component: Property,
    },
    {
      entityName: 'Region',
      foreignKeyField: 'locationId',
      localizationKey: 'Planning::Entity.Region',
      component: Region,
    },
    {
      entityName: 'Register',
      foreignKeyField: 'locationId',
      localizationKey: 'Planning::Entity.Register',
      component: Register,
    },
    {
      entityName: 'Stand',
      foreignKeyField: 'locationId',
      localizationKey: 'Planning::Entity.Stand',
      component: Stand,
    },
    {
      entityName: 'ThirdPartyProperty',
      foreignKeyField: 'locationId',
      localizationKey: 'Planning::Entity.ThirdPartyProperty',
      component: ThirdPartyProperty,
    },
    {
      entityName: 'EleIs',
      foreignKeyField: 'locationId',
      localizationKey: 'Planning::Entity.ELEIs',
      component: EleIs,
    },
    {
      entityName: 'Fence',
      foreignKeyField: 'locationId',
      localizationKey: 'Planning::Entity.Fence',
      component: Fence,
    },
    {
      entityName: 'FirebreakBlock',
      foreignKeyField: 'locationId',
      localizationKey: 'Planning::Entity.FirebreakBlock',
      component: FirebreakBlock,
    },
    {
      entityName: 'Helmet',
      foreignKeyField: 'locationId',
      localizationKey: 'Planning::Entity.Helmet',
      component: Helmet,
    },
    {
      entityName: 'InternalRoad',
      foreignKeyField: 'locationId',
      localizationKey: 'Planning::Entity.InternalRoad',
      component: InternalRoad,
    },
    {
      entityName: 'Office',
      foreignKeyField: 'locationId',
      localizationKey: 'Planning::Entity.Office',
      component: Office,
    },
    {
      entityName: 'CadastralSection',
      foreignKeyField: 'locationId',
      localizationKey: 'Planning::Entity.CadastralSection',
      component: CadastralSection,
    },
    {
      entityName: 'Pile',
      foreignKeyField: 'locationId',
      localizationKey: 'Planning::Entity.Pile',
      component: Pile,
    },
  
    {
      entityName: 'ThirdPartyDestination',
      foreignKeyField: 'locationId',
      localizationKey: 'Planning::Entity.ThirdPartyDestination',
      component: ThirdPartyDestination,
    }
  ],
};
